/* PublicLaunchGate — bloqueio temporário da área pública
 *
 * Estratégia:
 * - Busca GET /api/config/public-gate ao montar.
 * - Se gate desabilitado, renderiza children (rotas públicas normais).
 * - Se gate habilitado e usuário admin autenticado, renderiza children.
 * - Caso contrário, renderiza a tela de Gate com formulário de login
 *   restrito a administradores.
 *
 * Liga/desliga pela env `PUBLIC_LAUNCH_GATE_ENABLED` no backend.
 */
import { useEffect, useState } from "react";
import { Lock, Mail, Eye, EyeOff, ShieldAlert, LogOut } from "lucide-react";
import { Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatErr } from "@/lib/api";

const LOGO = "/assets/logo-nostradamus-oficial.png?v=3";

function isAdmin(user) {
  return !!user && user.perfil === "admin";
}

async function logGateEvent(event, extra = {}) {
  try {
    await api.post("/config/public-gate-event", {
      event,
      rota: typeof window !== "undefined" ? window.location.pathname : "",
      ...extra,
    });
  } catch (err) {
    // Não bloqueia a UI; log leve para diagnóstico
    console.warn(`[PublicLaunchGate] falha ao registrar evento ${event}:`, err?.message || err);
  }
}

export default function PublicLaunchGate() {
  const { user, loading, login, logout } = useAuth();
  const [gateStatus, setGateStatus] = useState({ loading: true, enabled: false });

  useEffect(() => {
    let mounted = true;
    api.get("/config/public-gate")
      .then(({ data }) => mounted && setGateStatus({ loading: false, enabled: !!data?.enabled }))
      .catch(() => mounted && setGateStatus({ loading: false, enabled: false }));
    return () => { mounted = false; };
  }, []);

  // Loading inicial — evita flash da home pública
  if (gateStatus.loading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-2 h-2 bg-[#0F1B2D] rounded-full animate-pulse" />
      </div>
    );
  }

  // Gate desabilitado → libera todas as rotas públicas
  if (!gateStatus.enabled) return <Outlet />;

  // Gate habilitado + usuário admin → libera
  if (isAdmin(user)) return <Outlet />;

  // Bloqueado: mostra tela do Gate com login restrito
  return <GateScreen user={user} login={login} logout={logout} />;
}

function GateScreen({ user, login, logout }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const isAuthenticatedNonAdmin = !!user && !isAdmin(user);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const u = await login(email, password);
      if (isAdmin(u)) {
        await logGateEvent("admin_login", { email: u.email, resultado: "success" });
        // useAuth state updates → reender libera Outlet
      } else {
        await logGateEvent("access_denied", { email: u.email, resultado: `perfil:${u.perfil}` });
        setErr("Este acesso está temporariamente restrito a administradores do Projeto Nostradamus.");
      }
    } catch (ex) {
      await logGateEvent("access_denied", { email, resultado: "invalid_credentials" });
      setErr(formatErr(ex));
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await logGateEvent("admin_logout", { email: user?.email });
    await logout();
    setEmail(""); setPassword(""); setErr("");
  };

  return (
    <div className="min-h-screen bg-white text-[#0F1B2D] flex flex-col" data-testid="public-launch-gate">
      {/* Top institutional bar */}
      <div className="bg-[#0F1B2D] text-white text-[11px] py-1.5 px-6 flex items-center justify-between tracking-wider uppercase font-mono">
        <span>Marinha do Brasil · Escola de Guerra Naval · LSC</span>
        <span className="hidden sm:inline">Acesso Restrito</span>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {/* Logo institucional centralizado */}
          <div className="flex flex-col items-center text-center mb-6">
            <img
              src={LOGO}
              alt="Projeto Nostradamus"
              className="w-32 h-32 sm:w-36 sm:h-36 object-contain rounded-full shadow-md"
              data-testid="gate-logo"
            />
            <h1 className="font-display text-2xl sm:text-3xl font-bold mt-5 tracking-tight">
              Projeto Nostradamus
            </h1>
            <p className="text-[#8c6a1f] text-sm font-semibold uppercase tracking-[0.18em] mt-1">
              Acesso Restrito
            </p>
            <p className="text-[15px] text-[#37486a] mt-4 leading-relaxed max-w-sm">
              O Projeto Nostradamus está em fase final de validação institucional.
              O acesso à página pública está temporariamente restrito a administradores autorizados.
            </p>
          </div>

          {/* Card de login */}
          <div className="bg-white border border-[#e3e8f0] rounded-xl shadow-sm p-6 sm:p-7 space-y-4" data-testid="gate-card">
            {isAuthenticatedNonAdmin ? (
              <div className="space-y-4">
                <div className="flex items-start gap-3 text-sm bg-amber-50 border border-amber-200 rounded p-3">
                  <ShieldAlert size={18} className="text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-amber-900" data-testid="gate-non-admin-message">
                    Este acesso está temporariamente restrito a administradores do Projeto Nostradamus.
                    Você está autenticado como <strong>{user?.email}</strong> ({user?.perfil}).
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-[#0F1B2D] text-[#0F1B2D] font-semibold text-sm hover:bg-[#0F1B2D] hover:text-white transition"
                  data-testid="gate-logout-button"
                >
                  <LogOut size={14} /> Sair e tentar com outra conta
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="gate-form">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-[#37486a] mb-1.5">
                    E-mail institucional
                  </label>
                  <div className="relative">
                    <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a96ad]" />
                    <input
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@instituicao.br"
                      className="w-full pl-9 pr-3 py-2.5 border border-[#cbd3e1] rounded-lg text-sm focus:outline-none focus:border-[#0F1B2D] focus:ring-2 focus:ring-[#0F1B2D]/15"
                      data-testid="gate-email-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-[#37486a] mb-1.5">
                    Senha
                  </label>
                  <div className="relative">
                    <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a96ad]" />
                    <input
                      type={showPwd ? "text" : "password"}
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full pl-9 pr-9 py-2.5 border border-[#cbd3e1] rounded-lg text-sm focus:outline-none focus:border-[#0F1B2D] focus:ring-2 focus:ring-[#0F1B2D]/15"
                      data-testid="gate-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a96ad] hover:text-[#0F1B2D]"
                      aria-label={showPwd ? "Ocultar senha" : "Mostrar senha"}
                    >
                      {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                {err && (
                  <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2.5" data-testid="gate-error">
                    {err}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#0F1B2D] hover:bg-[#1a2945] text-white font-semibold text-sm py-3 rounded-lg transition disabled:opacity-60"
                  data-testid="gate-submit-button"
                >
                  {submitting ? "Verificando…" : "Entrar como Administrador"}
                </button>

                <p className="text-[11px] text-[#5e6b85] text-center mt-2">
                  Acesso permitido somente a administradores autorizados.
                </p>
              </form>
            )}
          </div>

          <p className="text-[11px] text-[#5e6b85] text-center mt-6 tracking-wide">
            © Projeto Nostradamus · LSC · EGN · Marinha do Brasil
          </p>
        </div>
      </div>
    </div>
  );
}
